import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaUser, FaArrowRight } from 'react-icons/fa';

const NewsPage = () => {
  // News articles data
  const newsArticles = [
    {
      id: 1,
      title: 'Workshop at National Education Institute',
      excerpt: 'A workshop was conducted for selected young school inventors and their teachers at the NIE. The event focused on introducing robotics and STEM concepts to educators.',
      content: 'A workshop was conducted for selected young school inventors and their teachers at the National Institute of Education (NIE). The STEM Labs team demonstrated various robotics projects and provided hands-on activities for participants. Teachers learned how to incorporate STEM concepts into their classroom teaching, while students had the opportunity to work with advanced robotics kits. The workshop was part of NIE\'s initiative to promote innovative teaching methods.',
      image: '/images/news/nie.jpg',
      date: 'Dec 02, 2019',
      author: 'Admin',
      link: 'https://www.facebook.com/STEMLabSL/'
    },
    {
      id: 2,
      title: 'Robotics Workshop With LIONs Club',
      excerpt: 'STEM Robotics Workshop was held at Sella Katharagama Maha Vidyalaya in collaboration with the LIONs Club to inspire rural students.',
      content: 'STEM Robotics Workshop was held at Sella Katharagama Maha Vidyalaya. The event was organized in collaboration with the LIONs Club to bring STEM education to rural areas. Students were introduced to basic robotics concepts and had the opportunity to build and program simple robots. The workshop was a great success with enthusiastic participation from students who had never experienced robotics before.',
      image: '/images/news/lion.jpg',
      date: 'July 27, 2019',
      author: 'Admin',
      link: 'https://www.facebook.com/STEMLabSL/'
    },
    {
      id: 3,
      title: 'SLASS Exhibition at University of Colombo',
      excerpt: 'STEM Labs was placed a stall to introduce the STEMTronics Educational Kits at the Sri Lanka Association for the Advancement of Science (SLASS) exhibition.',
      content: 'STEM Labs was placed a stall to introduce the STEMTronics Educational Kits at the Sri Lanka Association for the Advancement of Science (SLASS) exhibition held at the University of Colombo. Our team demonstrated how these kits can be used to teach electronics concepts in an engaging way. The STEMTronics kits received significant interest from educators and students alike, with many schools expressing interest in incorporating them into their science curriculum.',
      image: '/images/news/slass.jpg',
      date: 'Dec 13, 2019',
      author: 'Admin',
      link: 'https://www.facebook.com/STEMLabSL/'
    },
    {
      id: 4,
      title: 'SLASS Exhibition Second Day Highlights',
      excerpt: 'STEM Labs continued its presence at the SLASS Exhibition, inspiring students with hands-on robotics demonstrations and activities.',
      content: 'On the second day of the SLASS Exhibition at the University of Colombo, STEM Labs continued to inspire visitors with interactive robotics demonstrations. Our team engaged with hundreds of students, allowing them to control robots and learn about the technology behind them. We also conducted mini-workshops throughout the day, giving participants a taste of what our comprehensive courses offer. The response was overwhelmingly positive, with many students expressing newfound interest in STEM fields.',
      image: '/images/news/slass1.jpg',
      date: 'Dec 14, 2019',
      author: 'Admin',
      link: 'https://www.facebook.com/STEMLabSL/'
    },
    {
      id: 5,
      title: 'STEMTronics Workshop at STEMLab',
      excerpt: 'STEMTronics workshop was held at STEMLab to inspire interest in electronics among students from various schools.',
      content: 'STEMTronics workshop was held at STEMLab to inspire interest in electronics among students. Participants learned about basic electronic components and circuits through hands-on activities. Each student built their own electronic project that they could take home. The workshop covered topics such as resistors, capacitors, LEDs, and basic circuit design. Students from multiple schools participated in the event, making it a diverse learning environment. The success of this workshop has prompted us to schedule similar events regularly throughout the year.',
      image: '/images/news/stemkit.jpg',
      date: 'Dec 26, 2019',
      author: 'Admin',
      link: 'https://www.facebook.com/STEMLabSL/'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Latest News | STEM Labs</title>
        <meta name="description" content="Stay updated with the latest news, events, and activities from STEM Labs" />
      </Helmet>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            Latest News
          </h1>
          <p className="text-white text-xl max-w-3xl mx-auto text-center">
            Stay updated with our latest events, achievements, and activities
          </p>
        </div>
      </div>
      
      {/* News Articles */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {newsArticles.map((article, index) => (
              <motion.article 
                key={article.id}
                className="bg-white rounded-lg shadow-md overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="md:flex h-full">
                  <div className="md:w-2/5">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="w-full h-full object-cover object-center"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x300?text=STEM+Labs+News';
                      }}
                    />
                  </div>
                  <div className="md:w-3/5 p-6 flex flex-col justify-between">
                    <div>
                      <h2 className="text-2xl font-bold mb-3 text-blue-800">{article.title}</h2>
                      <div className="flex items-center text-gray-500 text-sm mb-4">
                        <div className="flex items-center mr-4">
                          <FaCalendarAlt className="mr-1" />
                          <span>{article.date}</span>
                        </div>
                        <div className="flex items-center">
                          <FaUser className="mr-1" />
                          <span>{article.author}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4">{article.excerpt}</p>
                    </div>
                    <div>
                      <a 
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 font-medium hover:text-blue-800 transition-colors"
                      >
                        Read more <FaArrowRight className="ml-1" size={14} />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
      
      {/* Subscribe Section */}
      <section className="py-12 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Stay Updated</h2>
            <p className="text-gray-600 mb-8">
              Subscribe to our newsletter to receive the latest news, event announcements, and STEM resources directly in your inbox.
            </p>
            <form className="flex flex-col md:flex-row md:space-x-2 max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="Your email address" 
                className="flex-1 px-4 py-3 mb-2 md:mb-0 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button 
                type="submit"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Subscribe
              </button>
            </form>
            <p className="text-xs text-gray-500 mt-4">
              By subscribing, you agree to receive emails from STEM Labs. You can unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>
      
      {/* Social Media Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">Follow Us on Social Media</h2>
            <p className="text-gray-600">Stay connected for daily updates and STEM education resources</p>
          </div>
          <div className="flex justify-center space-x-4">
            <a 
              href="https://www.facebook.com/STEMLabSL/" 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
              </svg>
            </a>
            <a 
              href="https://www.instagram.com/_stemlabs.edu.lk_/" 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-tr from-purple-600 via-pink-500 to-orange-400 text-white p-3 rounded-full hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default NewsPage; 